import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { ApiDocumentSource } from '@izimi/api';
import { useAuth } from '@izimi/auth';
import type { Filters, FilterStoreSlices } from '@izimi/core';
import { useDocumentFiltersStore } from '@izimi/core';
import type { PagedDocumentsArgs } from '@izimi/modules';

type DocumentFiltersContextProps = ReturnType<typeof useDocumentFiltersStore> & {
  citizenSupportedFilters: Array<keyof Filters>;
  citizenDocumentArgs: PagedDocumentsArgs;
  currentFilter: keyof FilterStoreSlices;
};

const DocumentFiltersContext = createContext<DocumentFiltersContextProps | null>(null);

export const useDocumentFiltering = () => {
  const context = useContext(DocumentFiltersContext);
  if (!context) {
    throw new Error('useDocumentFiltering must be used within a DocumentFiltersProvider');
  }
  return context;
};

const citizenFilters: Record<keyof FilterStoreSlices, Array<keyof Filters>> = {
  vault: ['sources', 'receiverIds', 'labels'],
  byYou: ['sources', 'receiverIds', 'labels'],
  fromContacts: ['ownerIds', 'labels'],
};

const citizenDocumentArgs: Record<keyof FilterStoreSlices, PagedDocumentsArgs> = {
  vault: {
    type: 'vaultList',
  },
  byYou: {
    params: {
      isSharedByMe: true,
      states: 'ACTIVE',
    },
    type: 'sharedByYouList',
  },
  fromContacts: {
    params: {
      sources: ApiDocumentSource.Shared,
      states: ['ACTIVE', 'PENDING'],
    },
    type: 'sharedToYouList',
  },
};

const getFilterForPathname = (pathname: string): keyof FilterStoreSlices => {
  switch (pathname) {
    case '/sharing/to-you':
      return 'fromContacts';
    case '/sharing/by-you':
      return 'byYou';
    default:
      return 'vault';
  }
};

export const DocumentFiltersProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth();
  const { pathname } = useLocation();

  const currentFilter = getFilterForPathname(pathname);
  const filters = useDocumentFiltersStore(currentFilter);

  const citizenDocumentArgsWithOwner = useMemo<PagedDocumentsArgs>(() => {
    const args = citizenDocumentArgs[currentFilter];

    if (currentFilter === 'vault') {
      return {
        ...args,
        params: { ...args.params, ownerId: user?.profile.id },
      };
    }

    return args;
  }, [currentFilter, user?.profile.id]);

  const value = useMemo<DocumentFiltersContextProps>(
    () => ({
      ...filters,
      citizenSupportedFilters: citizenFilters[currentFilter],
      citizenDocumentArgs: citizenDocumentArgsWithOwner,
      currentFilter,
    }),
    [filters, currentFilter, citizenDocumentArgsWithOwner],
  );

  return (
    <DocumentFiltersContext.Provider value={value}>{children}</DocumentFiltersContext.Provider>
  );
};
